import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { whereFilterListToJsonArray } from "src/helpers/helperService";

export const downloadAutomationRecordsExport = (
  autoConfigId,
  callback,
  whereFilters = [],
  sortBy = null,
  format = "csv",
  // "multiclass" for the single-records tab, "non_multiclass" for the
  // multi-output tab; defaults to "all" so existing callers keep their
  // current behavior.
  predictionFilter = "all",
) => {
  let params = new URLSearchParams();
  let filt = whereFilterListToJsonArray(whereFilters);
  let hasInvalidFilters = false;
  filt.forEach((fil) => {
    if (fil.filter_on.name === "" || fil.filter_on.column === "") {
      hasInvalidFilters = true;
    }
  });
  if (!hasInvalidFilters) {
    params.append("filt", JSON.stringify(filt));
  }
  params.append("automation_config_template_id", autoConfigId);
  params.append("expand_json_columns", true);
  params.append("format", format);
  if (predictionFilter && predictionFilter !== "all") {
    params.append("prediction_filter", predictionFilter);
  }
  if (sortBy) {
    // convert to snake case
    let snakeCaseSortBy = { ...sortBy };
    const { isAscending } = snakeCaseSortBy;
    delete snakeCaseSortBy.isAscending;
    snakeCaseSortBy.is_ascending = isAscending;
    params.append("sort_by", JSON.stringify(snakeCaseSortBy));
  }
  axios({
    url: `${nuclearn_api_url}/products/cap-screening-automation/automation-record-export`,
    params: params,
    method: "GET",
    responseType: "blob",
  })
    .then((response) => {
      const isXlsx = format === "xlsx";
      const blobParts = isXlsx
        ? [response.data]
        : // BOM keeps Excel happy with UTF-8 CSVs
          [new Uint8Array([0xef, 0xbb, 0xbf]), response.data];
      const blobType = isXlsx
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/plain;charset=utf8";
      const url = window.URL.createObjectURL(
        new Blob(blobParts, { type: blobType }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `automation-record_${autoConfigId}_data.${isXlsx ? "xlsx" : "csv"}`,
      );
      document.body.appendChild(link);
      link.click();
      // Release the object URL and detach the link so repeated downloads
      // don't leak blob URLs or stack detached <a> nodes.
      link.remove();
      window.URL.revokeObjectURL(url);
    })
    .finally(() => {
      if (callback) {
        callback();
      }
    });
};

export const getTestrunRecords = (automationConfigId, filters) => {
  return axios({
    url: `${nuclearn_api_url}/products/cap-screening-automation/testrun/records`,
    data: {
      automation_config_id: automationConfigId,
      filters: filters,
    },
    method: "POST",
  }).then((response) => {
    return response.data;
  });
};

export const getTestrunDataRecords = (testRunId) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/cap-screening-automation/testrun/datarecords?test_run_id=${testRunId}`,
    )
    .then((response) => {
      return response.data;
    });
};

export const useGetTestrunDataRecords = (testRunId) => {
  return useQuery(
    ["testrunrecords", testRunId],
    () => getTestrunDataRecords(testRunId),
    {
      refetchOnWindowFocus: false,
      enabled: !!testRunId,
    },
  );
};

export const downloadTestrunRecords = (
  automationConfigId,
  filters,
  callback,
) => {
  axios({
    url: `${nuclearn_api_url}/products/cap-screening-automation/testrun/records`,
    data: {
      automation_config_id: automationConfigId,
      as_csv: true,
      filters: filters,
    },
    method: "POST",
    responseType: "blob",
  })
    .then((response) => {
      const BOM = new Uint8Array([0xef, 0xbb, 0xbf]); //https://stackoverflow.com/questions/6672834/specifying-blob-encoding-in-google-chrome
      const url = window.URL.createObjectURL(
        new Blob([BOM, response.data], { type: "text/plain;charset=utf8" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `testrundata-autoconfig${automationConfigId}-data.csv`,
      ); //or any other extension
      document.body.appendChild(link);
      link.click();
    })
    .finally(() => {
      if (callback) {
        callback();
      }
    });
};

const getAllAutomationRecords = (
  automationConfigTemplateId,
  page,
  numRecsPerPg = 10,
  whereFilters = [],
  isAdminConsole = false,
  sortBy = null,
) => {
  let params = new URLSearchParams();
  params.append("automation_config_template_id", automationConfigTemplateId);
  params.append("record_idx", (page - 1) * numRecsPerPg); // page * numrecspp gives record_idx
  params.append("num_recs_per_pg", numRecsPerPg);
  params.append("is_admin_console", isAdminConsole);
  let filt = whereFilterListToJsonArray(whereFilters);
  let hasInvalidFilters = false;
  filt.forEach((fil) => {
    if (fil.filter_on.name === "" || fil.filter_on.column === "") {
      hasInvalidFilters = true;
    }
  });
  if (!hasInvalidFilters) {
    params.append("filt", JSON.stringify(filt));
  }
  if (sortBy) {
    // convert to snake case
    let snakeCaseSortBy = { ...sortBy };
    const { isAscending } = snakeCaseSortBy;
    delete snakeCaseSortBy.isAscending;
    snakeCaseSortBy.is_ascending = isAscending;
    params.append("sort_by", JSON.stringify(snakeCaseSortBy));
  }
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-records",
      {
        params: params,
      },
    )
    .then((res) => {
      return res.data;
    });
};

export const useAllAutomationRecords = (
  automationConfigTemplateId,
  page,

  numRecsPerPg = 10,

  whereFilters = [],
  isAdminConsole,
  sortBy = null,
) => {
  return useQuery(
    [
      "allAutomationRecords",
      automationConfigTemplateId,
      page,
      numRecsPerPg,
      JSON.stringify(whereFilters),
      isAdminConsole,
      JSON.stringify(sortBy),
    ],
    () =>
      getAllAutomationRecords(
        automationConfigTemplateId,
        page,
        numRecsPerPg,
        whereFilters,
        isAdminConsole,
        sortBy,
      ),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );
};

const getFieldsFromLatestAutomationDataRecord = (
  automationConfigTemplateId,
) => {
  let params = new URLSearchParams();
  params.append("automation_config_template_id", automationConfigTemplateId);

  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-records/fields",
      {
        params: params,
      },
    )
    .then((res) => {
      return res.data;
    });
};

export const useGetFieldsFromLatestAutomationDataRecord = (
  automationConfigTemplateId,
) => {
  return useQuery(
    ["fieldsFromLatestAutomationDataRecord", automationConfigTemplateId],
    () => getFieldsFromLatestAutomationDataRecord(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
    },
  );
};

export const deleteAutomationRecordByList = (automationRecordList) => {
  if (!automationRecordList) {
    return;
  }
  return axios
    .post(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-records/soft-delete",
      automationRecordList,
    )
    .then((res) => res.data);
};

export const useDeleteAutomationRecordByList = (
  automationRecordList,
  automationConfigTemplateId,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteAutomationRecordByList, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        "allAutomationRecords",
        automationConfigTemplateId,
      ]);
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getAutomationRecordDetail = (automationRecordId) => {
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-record-detail",
      { params: { id: automationRecordId } },
    )
    .then((res) => res.data);
};

export const useAutomationRecordDetail = (automationRecordId, enabled) => {
  return useQuery(
    ["automationRecordDetails", automationRecordId],
    () => getAutomationRecordDetail(automationRecordId),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
    },
  );
};

const getAutomationEfficiency = (automationConfigTemplateId) => {
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-efficiency",
      {
        params: { automation_config_template_id: automationConfigTemplateId },
      },
    )
    .then((res) => res.data);
};

export const useAutomationEfficiency = (automationConfigTemplateId) => {
  return useQuery(
    ["automationEfficiency", automationConfigTemplateId],
    () => getAutomationEfficiency(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const getAutomationAccuracy = (automationConfigTemplateId) => {
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-accuracy",
      {
        params: { automation_config_template_id: automationConfigTemplateId },
      },
    )
    .then((res) => res.data);
};

export const useAutomationAccuracy = (automationConfigTemplateId) => {
  return useQuery(
    ["automationAccuracy", automationConfigTemplateId],
    () => getAutomationAccuracy(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const getAutomationCount = (automationConfigTemplateId) => {
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-record-count",
      {
        params: { automation_config_template_id: automationConfigTemplateId },
      },
    )
    .then((res) => res.data);
};

export const useAutomationCount = (automationConfigTemplateId) => {
  return useQuery(
    ["automationCount", automationConfigTemplateId],
    () => getAutomationCount(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const getAutomationKPIDashboard = (
  automationConfigTemplateId,
  { startDate, endDate, timeBucket = "week" } = {},
  whereFilters = [],
  includeConfusionMatrix = true,
) => {
  let params = new URLSearchParams();
  params.append("automation_config_template_id", automationConfigTemplateId);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  params.append("time_bucket", timeBucket);
  params.append("include_confusion_matrix", includeConfusionMatrix);

  let filters = whereFilterListToJsonArray(whereFilters);
  let hasInvalidFilters = false;
  filters.forEach((filt) => {
    if (filt.filter_on.name === "" || filt.filter_on.column === "") {
      hasInvalidFilters = true;
    }
  });
  if (!hasInvalidFilters) {
    params.append("filt", JSON.stringify(filters));
  }

  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/automation-kpi-dashboard",
      { params },
    )
    .then((res) => res.data);
};

export const useAutomationKPIDashboard = (
  automationConfigTemplateId,
  dateRange = {},
  whereFilters = [],
  includeConfusionMatrix = true,
  enabled = true,
) => {
  return useQuery(
    [
      "automationKPIDashboard",
      automationConfigTemplateId,
      JSON.stringify(dateRange),
      JSON.stringify(whereFilters),
      includeConfusionMatrix,
    ],
    () =>
      getAutomationKPIDashboard(
        automationConfigTemplateId,
        dateRange,
        whereFilters,
        includeConfusionMatrix,
      ),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: enabled && !!automationConfigTemplateId,
    },
  );
};

export const getCAPAutomationConfigById = (acId) => {
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/?automation_config_id=" +
        acId,
    )
    .then((response) => {
      return response.data;
    });
};

export const useAutomationTemplateByID = (acId) => {
  return useQuery(
    ["automationTemplateById", acId],
    () => getCAPAutomationConfigById(acId),
    {
      enabled: acId !== null,
      refetchOnWindowFocus: false,
    },
  );
};

export const patchAutomationTemplate = (automationTemplate) => {
  return axios.patch(nuclearn_api_url + "/products/cap-screening-automation/", {
    ...automationTemplate,
  });
};

// Similar CRs Feature

const getSimilarCRs = (
  automationRecordId,
  datasetId,
  outputFieldName,
  maxResults = 10,
) => {
  return axios
    .post(`${nuclearn_api_url}/products/cap-screening-automation/similar-crs`, {
      automation_record_id: automationRecordId,
      dataset_id: datasetId,
      output_field_name: outputFieldName,
      max_results: maxResults,
    })
    .then((res) => res.data);
};

export const useSimilarCRs = (
  automationRecordId,
  datasetId,
  outputFieldName,
  maxResults = 10,
  enabled = false,
) => {
  return useQuery(
    ["similarCRs", automationRecordId, datasetId, outputFieldName, maxResults],
    () =>
      getSimilarCRs(automationRecordId, datasetId, outputFieldName, maxResults),
    {
      enabled: enabled && !!datasetId && !!outputFieldName,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );
};
